import { OptimizationService, OptimizationResult } from './OptimizationService';
import {
  getFirestore,
  collection,
  doc,
  setDoc,
  getDoc,
  Timestamp,
  query,
  where,
  getDocs
} from 'firebase/firestore';
import { dataApp } from '../../config/firebase';

const db = getFirestore(dataApp);

interface BulkOptimization {
  id: string;
  productIds: string[];
  marketplace: string;
  promptId?: string;
  status: 'pending' | 'processing' | 'completed' | 'failed' | 'paused';
  startedAt: Date;
  completedAt?: Date;
  results: OptimizationResult[];
  error?: string;
  progress?: number;
}

export class BulkOptimizationService extends OptimizationService {
  private static bulkInstance: BulkOptimizationService;
  private queue: BulkOptimization[] = [];
  private activeWorkers = 0;
  private readonly MAX_WORKERS = 3;

  protected constructor() {
    super();
  }

  static getInstance(): BulkOptimizationService {
    if (!BulkOptimizationService.bulkInstance) {
      BulkOptimizationService.bulkInstance = new BulkOptimizationService();
    }
    return BulkOptimizationService.bulkInstance;
  }

  async enqueueBulkOptimization(
    productIds: string[],
    marketplace: string,
    promptId?: string
  ): Promise<string> {
    const id = await this.createBulkOptimization(productIds, marketplace, promptId);
    this.queue.push({
      id,
      productIds,
      marketplace,
      promptId,
      status: 'pending',
      startedAt: new Date(),
      results: []
    });
    this.processQueue();
    return id;
  }

  private completionCallbacks: (() => void)[] = [];
  private productCompletionCallbacks: ((productId: string, total: number, current: number) => void)[] = [];

  onComplete(callback: () => void) {
    this.completionCallbacks.push(callback);
  }

  onProductComplete(callback: (productId: string, total: number, current: number) => void) {
    this.productCompletionCallbacks.push(callback);
  }

  private async processQueue(): Promise<void> {
    while (this.queue.length > 0 && this.activeWorkers < this.MAX_WORKERS) {
      const bulkOptimization = this.queue.shift();
      if (bulkOptimization) {
        this.activeWorkers++;
        
        // Run optimization in background without awaiting
        this.processBulkOptimization(bulkOptimization.id)
          .then(() => {
            // Update UI through state management if needed
            this.notifyProgress(bulkOptimization.id, 'completed');
          })
          .catch((error) => {
            this.notifyProgress(bulkOptimization.id, 'failed', error);
          })
          .finally(() => {
            this.activeWorkers--;
            
            // If queue is empty and no active workers, notify completion
            if (this.queue.length === 0 && this.activeWorkers === 0) {
              // Delay completion callback to ensure all product notifications are shown first
              setTimeout(() => {
                this.completionCallbacks.forEach(cb => cb());
                this.completionCallbacks = []; // Clear callbacks after execution
              }, 1000);
            }
            
            if (this.queue.length > 0) {
              this.processQueue();
            }
          });
      }
    }
  }

  private notifyProgress(id: string, status: 'completed' | 'failed', error?: Error) {
    // Remove console.log to prevent cluttering the console
    // Progress notifications are now handled through toast
  }

  async getQueueStatus(): Promise<{
    pending: number;
    processing: number;
    completed: number;
    failed: number;
  }> {
    const q = query(this.getBulkOptimizationsRef());
    const querySnapshot = await getDocs(q);
    
    const statusCounts = {
      pending: 0,
      processing: 0,
      completed: 0,
      failed: 0
    };

    querySnapshot.forEach(doc => {
      const status = doc.data().status;
      const validStatus = status as keyof typeof statusCounts;
      if (validStatus in statusCounts) {
        statusCounts[validStatus]++;
      }
    });

    return statusCounts;
  }

  async getActiveOptimizations(): Promise<BulkOptimization[]> {
    const q = query(
      this.getBulkOptimizationsRef(),
      where('status', 'in', ['processing', 'paused'])
    );
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => ({
      ...doc.data(),
      startedAt: doc.data().startedAt.toDate(),
      completedAt: doc.data().completedAt?.toDate()
    })) as BulkOptimization[];
  }

  async getQueueLength(): Promise<number> {
    const q = query(
      this.getBulkOptimizationsRef(),
      where('status', '==', 'pending')
    );
    const querySnapshot = await getDocs(q);
    return querySnapshot.size;
  }

  private getBulkOptimizationsRef() {
    return collection(db, 'bulk_optimizations');
  }

  async createBulkOptimization(
    productIds: string[],
    marketplace: string,
    promptId?: string
  ): Promise<string> {
    const bulkOptimizationRef = doc(this.getBulkOptimizationsRef());
    const bulkOptimization: BulkOptimization = {
      id: bulkOptimizationRef.id,
      productIds,
      marketplace,
      promptId,
      status: 'pending',
      startedAt: new Date(),
      results: []
    };

    await setDoc(bulkOptimizationRef, {
      ...bulkOptimization,
      startedAt: Timestamp.fromDate(bulkOptimization.startedAt)
    });

    return bulkOptimizationRef.id;
  }

  private readonly RATE_LIMIT = 3; // Requests per second
  private readonly BATCH_SIZE = 5; // Products per batch
  private isPaused = false;

  async pauseBulkOptimization(): Promise<void> {
    this.isPaused = true;
  }

  async resumeBulkOptimization(): Promise<void> {
    this.isPaused = false;
  }

  private async processProductWithRateLimit(
    productId: string,
    marketplace: string,
    promptId?: string,
    currentIndex?: number,
    totalProducts?: number
  ): Promise<OptimizationResult> {
    let attempt = 0;
    const maxAttempts = 5;
    
    while (attempt < maxAttempts) {
      try {
        const result = await this.optimizeProduct(
          productId,
          marketplace,
          promptId || ''
        );
        // Notify about individual product completion if indices are provided
        if (currentIndex !== undefined && totalProducts !== undefined) {
          this.productCompletionCallbacks.forEach(cb =>
            cb(productId, totalProducts, currentIndex + 1)
          );
        }
        return result;
      } catch (error) {
        attempt++;
        if (attempt >= maxAttempts) {
          return {
            productId,
            marketplace,
            content: {
              title: '',
              description: '',
              bulletPoints: []
            },
            prompt: {
              id: promptId || 'default',
              title: 'Unknown'
            },
            timestamp: new Date(),
            status: 'error',
            error: error instanceof Error ? error.message : 'Unknown error'
          };
        }
        // Wait before retrying - capture attempt in closure
        const currentAttempt = attempt;
        await new Promise(resolve => setTimeout(resolve, 1000 * currentAttempt));
      }
    }
    
    // This should never be reached
    return {
      productId,
      marketplace,
      content: {
        title: '',
        description: '',
        bulletPoints: []
      },
      prompt: {
        id: promptId || 'default',
        title: 'Unknown'
      },
      timestamp: new Date(),
      status: 'error',
      error: 'Max retry attempts reached'
    };
  }

  async processBulkOptimization(bulkOptimizationId: string): Promise<void> {
    const bulkOptimizationRef = doc(this.getBulkOptimizationsRef(), bulkOptimizationId);
    
    // Update status to processing
    await setDoc(bulkOptimizationRef, {
      status: 'processing'
    }, { merge: true });

    try {
      // Get the bulk optimization document
      const bulkOptimizationDoc = await getDoc(bulkOptimizationRef);
      if (!bulkOptimizationDoc.exists()) {
        throw new Error('Bulk optimization not found');
      }
      const bulkOptimization = bulkOptimizationDoc.data() as BulkOptimization;

      const results: OptimizationResult[] = [];
      const totalProducts = bulkOptimization.productIds.length;
      let processedCount = 0;

      // Process in batches
      for (let i = 0; i < totalProducts; i += this.BATCH_SIZE) {
        if (this.isPaused) {
          await new Promise(resolve => {
            const interval = setInterval(() => {
              if (!this.isPaused) {
                clearInterval(interval);
                resolve(null);
              }
            }, 1000);
          });
        }

        const batch = bulkOptimization.productIds.slice(i, i + this.BATCH_SIZE);
        const batchPromises = batch.map((productId, batchIndex) =>
          this.processProductWithRateLimit(
            productId,
            bulkOptimization.marketplace,
            bulkOptimization.promptId,
            i + batchIndex,  // Current overall index
            totalProducts    // Total number of products
          )
        );

        const batchResults = await Promise.all(batchPromises);
        results.push(...batchResults);
        
        // Notify about each completed product
        for (const result of batchResults) {
          const currentProcessedCount = ++processedCount;
          this.productCompletionCallbacks.forEach(cb =>
            cb(result.productId, totalProducts, currentProcessedCount)
          );
        }

        // Update progress
        await setDoc(bulkOptimizationRef, {
          progress: Math.round((processedCount / totalProducts) * 100)
        }, { merge: true });

        // Rate limiting
        await new Promise(resolve => setTimeout(resolve, 1000 / this.RATE_LIMIT));
      }

      // Update status and results
      await setDoc(bulkOptimizationRef, {
        status: 'completed',
        completedAt: Timestamp.now(),
        results
      }, { merge: true });

    } catch (error) {
      await setDoc(bulkOptimizationRef, {
        status: 'failed',
        completedAt: Timestamp.now(),
        error: error instanceof Error ? error.message : 'Unknown error'
      }, { merge: true });
      throw error;
    }
  }
}

export const bulkOptimizationService = BulkOptimizationService.getInstance();
